import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useCreateProject, useUpdateProject } from "@workspace/api-client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MediaManager } from "./MediaManager";
import { RichTextEditor } from "./RichTextEditor";

const formSchema = z.object({
  title: z.string().min(2),
  slug: z.string().min(2),
  description: z.string().min(10),
  longDescription: z.string().min(10),
  category: z.string().min(2),
  clientName: z.string().optional(),
  clientIndustry: z.string().optional(),
  techStack: z.string(),
  liveUrl: z.string().url().or(z.literal("")).optional(),
  repoUrl: z.string().url().or(z.literal("")).optional(),
  result: z.string().optional(),
  featured: z.boolean().default(false),
});

interface ProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  project?: any; // If present, we are editing
  onSuccess: () => void;
}

export function ProjectDialog({ isOpen, onClose, project, onSuccess }: ProjectDialogProps) {
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      slug: "",
      description: "",
      longDescription: "",
      category: "",
      clientName: "",
      clientIndustry: "",
      techStack: "",
      liveUrl: "",
      repoUrl: "",
      result: "",
      featured: false,
    },
  });

  useEffect(() => {
    if (project) {
      form.reset({
        title: project.title,
        slug: project.slug,
        description: project.description || "",
        longDescription: project.longDescription || "",
        category: project.category,
        clientName: project.clientName || "",
        clientIndustry: project.clientIndustry || "",
        techStack: project.techStack || "",
        liveUrl: project.liveUrl || "",
        repoUrl: project.repoUrl || "",
        result: project.result || "",
        featured: project.featured || false,
      });
    } else {
      form.reset({
        title: "",
        slug: "",
        description: "",
        longDescription: "",
        category: "",
        clientName: "",
        clientIndustry: "",
        techStack: "",
        liveUrl: "",
        repoUrl: "",
        result: "",
        featured: false,
      });
    }
  }, [project, form, isOpen]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    try {
      if (project) {
        await updateProject.mutateAsync({ id: project.id, data });
      } else {
        await createProject.mutateAsync({ data });
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Failed to save project:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] p-0 flex flex-col max-h-[90vh]">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle>{project ? "Edit Project" : "Add New Project"}</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="details" className="flex-1 flex flex-col min-h-0">
          <div className="px-6 pt-2">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">Project Details</TabsTrigger>
              <TabsTrigger value="media" disabled={!project}>Photos & Gallery</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="details" className="flex-1 overflow-y-auto px-6 py-4 custom-scrollbar">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 pb-24">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl><Input placeholder="Project Title" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="slug"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Slug</FormLabel>
                        <FormControl><Input placeholder="project-slug" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category</FormLabel>
                        <FormControl><Input placeholder="e.g. Web Development" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="techStack"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tech Stack</FormLabel>
                        <FormControl><Input placeholder="React, Node, Postgres" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="clientName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Client Name</FormLabel>
                        <FormControl><Input placeholder="e.g. Icon Digital Art" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="clientIndustry"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Client Industry</FormLabel>
                        <FormControl><Input placeholder="e.g. Publicity Design" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="liveUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Live Project URL</FormLabel>
                        <FormControl><Input placeholder="https://..." {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="repoUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Repository URL</FormLabel>
                        <FormControl><Input placeholder="https://github.com/..." {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Short Description</FormLabel>
                      <FormControl><Input placeholder="Brief overview..." {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="result"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Project Result / Outcome</FormLabel>
                      <FormControl><Input placeholder="e.g. 300% improvement in load times" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="longDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Long Description</FormLabel>
                      <FormControl>
                        <div className="bg-background rounded-md border border-input overflow-hidden flex flex-col">
                          <RichTextEditor
                            theme="snow"
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Detailed project case study..."
                            modules={{
                              toolbar: [
                                [{ 'header': [1, 2, 3, false] }],
                                ['bold', 'italic', 'underline', 'strike'],
                                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                ['link', 'clean']
                              ],
                            }}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="featured"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Featured Project</FormLabel>
                        <p className="text-sm text-muted-foreground">This project will be highlighted on the homepage.</p>
                      </div>
                    </FormItem>
                  )}
                />

                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-background via-background to-transparent pt-12 flex justify-end gap-2 border-t border-border/10">
                  <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                  <Button type="submit" disabled={createProject.isPending || updateProject.isPending}>
                    {project ? "Save Changes" : "Create Project"}
                  </Button>
                </div>
              </form>
            </Form>
          </TabsContent>

          <TabsContent value="media" className="flex-1 overflow-y-auto px-6 py-4 custom-scrollbar">
            <MediaManager type="PROJECT" parentId={project?.id} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
